import {
  getUniqueDefaultAvatarDataUrl,
  isPlaceholderProfilePictureUrl,
} from '../../../../utils/uniqueDefaultAvatar';
import { getStarterClubLabel } from '../config/starterClubCatalog';
import { stripDiscussionHashtagTokens } from '../components/hashtags/discussionHashtagDisplay';
import {
  PERSONAL_STATUS_CATEGORY_KEY,
  POST_COMPOSE_CONTENT_TYPES,
  composeStoryAudienceHeadlineLabel,
} from '../config/postComposeConstants';

/** Prefer API label; otherwise backend starter catalog, then a readable slug. */
export function resolveClubDisplayLabel(slug, apiLabel) {
  const lab = String(apiLabel || '').trim();
  if (lab) return lab;
  const key = String(slug || '').trim();
  if (key === PERSONAL_STATUS_CATEGORY_KEY) return "What's going on";
  if (!key) return '';
  const starter = getStarterClubLabel(key);
  if (starter) return starter;
  return key.replace(/_/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());
}

export function categoriesFromHomeByCategory(byCategory) {
  if (!Array.isArray(byCategory) || !byCategory.length) return null;
  return byCategory.map((c) => ({
    key: c.key,
    label: c.label,
    thumbnailUrl: c.thumbnailUrl || '',
  }));
}

export function discussionOwnerUserId(topic) {
  if (!topic) return null;
  return topic.createdByUserId || topic.authorUserId || topic.userId || topic.authorSnapshot?.userId || null;
}

export function isDiscussionOwner(topic, profile) {
  const ownerId = discussionOwnerUserId(topic);
  if (ownerId == null || profile?.userId == null) return false;
  return String(ownerId).trim() === String(profile.userId).trim();
}

/** Whether a logged-in viewer should see Follow on another user's public status post (not club topics). */
export function threadShowsAuthorFollowAction(thread, profile) {
  if (!profile?.userId || !thread) return false;
  if (thread.postAnonymous) return false;
  if (isDiscussionOwner(thread, profile)) return false;
  if (!discussionOwnerUserId(thread)) return false;
  if (!isStatusPostThread(thread)) return false;
  const audience = String(thread.audience || 'public').trim().toLowerCase();
  return audience === 'public';
}

export function isDiscussionDraft(topic) {
  return String(topic?.status || '').trim().toLowerCase() === 'draft';
}

export function mergeHighlightMembers(apiMembers, extraMembers, max = 5) {
  const merged = [];
  const seen = new Set();
  for (const m of [...(apiMembers || []), ...(extraMembers || [])]) {
    const uid = String(m?.userId || '').trim();
    if (!uid || seen.has(uid)) continue;
    seen.add(uid);
    merged.push({
      userId: uid,
      name: m.name || 'Member',
      profilePictureUrl: m.profilePictureUrl || '',
    });
    if (merged.length >= max) break;
  }
  return merged;
}

/** Stable key for deduping discussion threads in feed lists. */
export function discussionThreadStableKey(thread) {
  const slug = String(thread?.slug || '').trim();
  if (slug) return `slug:${slug}`;
  const id = String(thread?._id || thread?.id || '').trim();
  if (id) return `id:${id}`;
  return '';
}

/** Append feed page rows without duplicating threads already on screen. */
export function appendUniqueDiscussionThreads(existing, batch) {
  const prev = Array.isArray(existing) ? existing : [];
  const items = Array.isArray(batch) ? batch : [];
  if (!items.length) return prev;
  const seen = new Set(prev.map((t) => discussionThreadStableKey(t)).filter(Boolean));
  const next = [...prev];
  for (const thread of items) {
    const key = discussionThreadStableKey(thread);
    if (key && seen.has(key)) continue;
    if (key) seen.add(key);
    next.push(thread);
  }
  return next;
}

export function authorSnapshotProfilePictureUrl(snap) {
  if (!snap) return '';
  return String(
    snap.profile_picture_url || snap.profilePictureUrl || snap.profilePicUrl || ''
  ).trim();
}

export function memberPreviewFromThread(t) {
  if (t?.postAnonymous) return null;
  const uid = String(t?.authorUserId || t?.authorSnapshot?.userId || '').trim();
  if (!uid) return null;
  return {
    userId: uid,
    name: t.authorSnapshot?.name || 'Member',
    profilePictureUrl: authorSnapshotProfilePictureUrl(t.authorSnapshot),
    profileMode: t.authorSnapshot?.profileMode || 'personal',
  };
}

/** Anonymous club post — author identity must not be shown in the feed or thread chrome. */
export function isAnonymousClubPost(thread) {
  return thread?.postAnonymous === true && threadUsesClubFeedIdentity(thread);
}

/** Feed / thread label for anonymous posts published to one or more clubs. */
export function anonymousClubPostIdentityLabel(thread, options = {}) {
  const clubCount =
    Number(options.clubCount) || resolveFeedClubIdentity(thread).clubCount || 1;
  if (clubCount <= 1) return 'Anonymous post in a club';
  return `Anonymous post in ${clubCount} clubs`;
}

/** Story / personal wall post (categoryKey __personal__). */
export function isStatusPostThread(t) {
  if (!t) return false;
  if (String(t.categoryKey || t.clubSlug || '').trim() === PERSONAL_STATUS_CATEGORY_KEY) return true;
  // Legacy rows may still carry postKind: 'status'.
  return String(t.postKind || '').trim().toLowerCase() === 'status';
}

/** Resolve feed badge type: Story, Question, Article, or Announcement. */
export function resolveThreadPostContentType(thread) {
  const stored = String(thread?.postContentType || '').trim().toLowerCase();
  if (POST_COMPOSE_CONTENT_TYPES.includes(stored)) return stored;
  if (isStatusPostThread(thread)) return 'story';
  return 'article';
}

export function isStoryContentThread(thread) {
  return resolveThreadPostContentType(thread) === 'story';
}

/** Thread view modal layout shared by story, question, and announcement (not article). */
export function usesStoryModalThreadLayout(thread) {
  const type = resolveThreadPostContentType(thread);
  return type === 'story' || type === 'question' || type === 'announcement';
}

export function storyShareClubSlugs(thread) {
  if (!thread) return [];
  const legacy = String(thread.shareClubSlug || '').trim();
  return [
    ...new Set(
      [
        ...(Array.isArray(thread.shareClubSlugs) ? thread.shareClubSlugs : []),
        ...(legacy ? [legacy] : []),
      ]
        .map((slug) => String(slug || '').trim())
        .filter(Boolean)
    ),
  ];
}

export function storyPrimaryClubSlug(thread) {
  return resolveStoryModalClubMeta(thread).slug;
}

export function isStorySharedToClubs(thread) {
  return storyShareClubSlugs(thread).length > 0;
}

/** Feed card identity: post published on the user's wall (not a club). */
export function isWallPostThread(thread) {
  if (!thread) return false;
  const categoryKey = String(thread.categoryKey || thread.clubSlug || '').trim();
  if (categoryKey && categoryKey !== PERSONAL_STATUS_CATEGORY_KEY) {
    return false;
  }
  return true;
}

/** Feed cards: show club header when categoryKey is a club or legacy personal + shareClubSlugs. */
export function threadUsesClubFeedIdentity(thread) {
  if (!thread) return false;
  if (!isWallPostThread(thread)) return true;
  return feedClubSlugsFromThread(thread).length > 0;
}

/** Primary club slug for feed card actions (join, club link). */
export function primaryFeedClubSlug(thread) {
  const slugs = feedClubSlugsFromThread(thread);
  return slugs[0] || '';
}

/** Club slugs shown on feed cards — primary category plus any shared clubs. */
export function feedClubSlugsFromThread(thread) {
  if (!thread) return [];
  const categoryKey = String(thread.categoryKey || thread.clubSlug || '').trim();
  if (categoryKey && categoryKey !== PERSONAL_STATUS_CATEGORY_KEY) {
    return [
      ...new Set(
        [categoryKey, ...storyShareClubSlugs(thread)]
          .map((slug) => String(slug || '').trim())
          .filter((slug) => slug && slug !== PERSONAL_STATUS_CATEGORY_KEY)
      ),
    ];
  }
  return storyShareClubSlugs(thread);
}

/** Club name/icon metadata for feed card headers (single or multi-club). */
export function resolveFeedClubIdentity(thread) {
  const slugs = feedClubSlugsFromThread(thread);
  if (!slugs.length) {
    return {
      clubCount: 0,
      clubs: [],
      primaryLabel: 'Club',
      primaryThumbnail: '',
      clubLabels: [],
    };
  }

  const shareSlugs = storyShareClubSlugs(thread);
  const categoryKey = String(thread.categoryKey || thread.clubSlug || '').trim();
  const hasClubCategory = categoryKey && categoryKey !== PERSONAL_STATUS_CATEGORY_KEY;
  const clubs = slugs.map((slug) => {
    let apiLabel = '';
    let thumbnailUrl = '';
    if (hasClubCategory && slug === categoryKey) {
      apiLabel = thread.clubLabel || thread.categoryLabel;
      thumbnailUrl = String(thread.clubThumbnailUrl || '').trim();
    } else if (slug === shareSlugs[0]) {
      apiLabel = thread.shareClubLabel;
      thumbnailUrl = String(thread.shareClubThumbnailUrl || '').trim();
    }
    return {
      slug,
      label: resolveClubDisplayLabel(slug, apiLabel) || slug,
      thumbnailUrl,
    };
  });

  return {
    clubCount: clubs.length,
    clubs,
    primaryLabel: clubs[0].label,
    primaryThumbnail: clubs[0].thumbnailUrl,
    clubLabels: clubs.map((club) => club.label),
  };
}

export function resolveStoryModalClubMeta(thread, contextClubSlug = '') {
  if (!thread) {
    return { slug: '', label: '', thumbnailUrl: '', clubCount: 0, showHeading: false };
  }

  const allSlugs = feedClubSlugsFromThread(thread);
  if (allSlugs.length > 0) {
    const feedClub = resolveFeedClubIdentity(thread);
    const primary = feedClub.clubs[0] || {
      slug: allSlugs[0],
      label: resolveClubDisplayLabel(allSlugs[0]),
      thumbnailUrl: '',
    };
    return {
      slug: primary.slug || allSlugs[0],
      label: primary.label || resolveClubDisplayLabel(allSlugs[0]),
      thumbnailUrl: primary.thumbnailUrl || '',
      clubCount: allSlugs.length,
      showHeading: true,
    };
  }

  const ctxSlug = String(contextClubSlug || '').trim();
  if (ctxSlug && ctxSlug !== PERSONAL_STATUS_CATEGORY_KEY) {
    return {
      slug: ctxSlug,
      label: resolveClubDisplayLabel(ctxSlug),
      thumbnailUrl: '',
      clubCount: 1,
      showHeading: true,
    };
  }

  return { slug: '', label: '', thumbnailUrl: '', clubCount: 0, showHeading: false };
}

export function isStoryPostedOnClub(thread, contextClubSlug = '') {
  return resolveStoryModalClubMeta(thread, contextClubSlug).showHeading;
}

/** Primary club slug for a story modal — thread data, then URL/view context. */
export function resolveStoryModalContextClubSlug(thread, contextClubSlug = '') {
  return resolveStoryModalClubMeta(thread, contextClubSlug).slug;
}

export function formatStoryPostedOnClubsLabel(clubCount) {
  const count = Number(clubCount) || 0;
  if (count <= 0) return '';
  if (count === 1) return 'posted on club';
  return 'posted on clubs';
}

/** Feed identity: only when the story was published to a club, not a wall post (even if shared). */
export function storyFeedPostedOnClubsLabel(thread) {
  if (!thread) return '';
  const categoryKey = String(thread.categoryKey || thread.clubSlug || '').trim();
  if (!categoryKey || categoryKey === PERSONAL_STATUS_CATEGORY_KEY) return '';
  return formatStoryPostedOnClubsLabel(resolveStoryModalClubMeta(thread).clubCount);
}

export function formatStoryModalClubsPillLabel(clubCount) {
  const count = Number(clubCount) || 0;
  if (count <= 0) return '';
  if (count === 1) return '1 club';
  return `${count} clubs`;
}

/** Multi-club story identity: "(2) clubs" with HoverInfo label. */
export function formatStoryPostedOnMultiClubsCountLabel(clubCount) {
  const count = Number(clubCount) || 0;
  if (count <= 1) return '';
  return `(${count}) clubs`;
}

/** Story media modal: club destination detail for "posted on …" line. */
export function resolveStoryPostedOnClubDetail(thread, contextClubSlug = '') {
  const clubMeta = resolveStoryModalClubMeta(thread, contextClubSlug);
  if (!clubMeta.showHeading) return null;

  const feedClub = resolveFeedClubIdentity(thread);
  if (!feedClub.clubCount) return null;

  if (feedClub.clubCount === 1) {
    return {
      type: 'single',
      clubName: feedClub.primaryLabel || clubMeta.label || 'Club',
      clubThumbnailUrl: feedClub.primaryThumbnail || clubMeta.thumbnailUrl || '',
    };
  }

  const clubLabels = feedClub.clubs
    .map((club) => String(club?.label || '').trim())
    .filter(Boolean);

  return {
    type: 'multi',
    clubCount: feedClub.clubCount,
    clubLabels: clubLabels.length ? clubLabels : feedClub.clubLabels,
    hoverLabel: formatStoryPostedOnMultiClubsCountLabel(feedClub.clubCount),
  };
}

/** Media lightbox header meta for feed/thread post galleries and body media. */
export function resolveDiscussionPostMediaModalMeta(thread, options = {}) {
  const {
    timeLabel = '',
    authorPreview = null,
    contextClubSlug = '',
    baseUrl: _baseUrl = '',
  } = options;

  if (!thread) {
    return { variant: 'club', clubName: '', clubThumbnailUrl: '', title: '' };
  }

  if (isStoryContentThread(thread)) {
    const preview = authorPreview || memberPreviewFromThread(thread);
    const statusContext = getStatusPostContextParts(thread, { variant: 'article' });
    const postedOnClubDetail = resolveStoryPostedOnClubDetail(thread, contextClubSlug);

    const isAnonymousClub = isAnonymousClubPost(thread);
    return {
      variant: 'story',
      authorName: isAnonymousClub
        ? anonymousClubPostIdentityLabel(thread)
        : thread?.postAnonymous
          ? 'Anonymous'
          : statusContext?.authorName || preview?.name || 'Member',
      authorAvatarSrc: isAnonymousClub || thread?.postAnonymous
        ? ''
        : String(preview?.profilePictureUrl || '').trim(),
      authorProfileMode: preview?.profileMode || 'personal',
      isAnonymous: isAnonymousClub || !!thread?.postAnonymous,
      postedOnClubDetail,
      timeLabel: String(timeLabel || '').trim(),
      locationName: String(statusContext?.locationName || '').trim(),
      withLine: String(statusContext?.withLine || '').trim(),
      baseUrl: _baseUrl,
    };
  }

  return {
    variant: 'club',
    clubName:
      resolveClubDisplayLabel(
        thread.categoryKey || thread.clubSlug,
        thread.clubLabel || thread.categoryLabel
      ) || 'Club',
    clubThumbnailUrl: String(thread.clubThumbnailUrl || '').trim(),
    title: String(thread.title || '').trim(),
  };
}

/** Centered modal title for story thread view. */
export function formatStoryModalTitle(authorName, clubCount, options = {}) {
  const { authorName: name, suffix } = formatStoryModalTitleParts(authorName, clubCount, options);
  return `${name}${suffix}`;
}

/** Split modal title for styled author highlight in chrome. */
export function formatStoryModalTitleParts(authorName, clubCount, options = {}) {
  const count = Number(clubCount) || 0;
  if (options.postAnonymous) {
    if (count <= 1) return { authorName: 'Anonymous post', suffix: ' in a club' };
    return { authorName: 'Anonymous post', suffix: ` in ${count} clubs` };
  }
  const name = String(authorName || '').trim() || 'Member';
  if (count <= 0) return { authorName: name, suffix: "'s post" };
  if (count === 1) return { authorName: name, suffix: "'s post on a club" };
  return { authorName: name, suffix: `'s post on ${count} clubs` };
}

/** Feed filter: match only on stored postContentType (story, question, article, announcement). */
export function threadPostContentTypeForFilter(thread) {
  const stored = String(thread?.postContentType || '').trim().toLowerCase();
  return POST_COMPOSE_CONTENT_TYPES.includes(stored) ? stored : null;
}

export function threadMatchesPostContentTypeFilters(thread, filterTypes) {
  if (!Array.isArray(filterTypes) || !filterTypes.length) return true;
  const type = threadPostContentTypeForFilter(thread);
  if (!type) return false;
  const allowed = new Set(
    filterTypes.map((value) => String(value || '').trim().toLowerCase()).filter(Boolean)
  );
  return allowed.has(type);
}

/** Title-case each word in a free-text location label. */
export function toTitleCaseWords(text) {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function taggedUserNamesFromThread(thread) {
  if (!thread) return [];
  if (Array.isArray(thread.taggedUsers) && thread.taggedUsers.length) {
    return thread.taggedUsers
      .map((u) => String(u?.name || u?.label || '').trim())
      .filter(Boolean);
  }
  return [];
}

export function threadHasTaggedPeople(thread) {
  if (!thread) return false;
  if (taggedUserNamesFromThread(thread).length > 0) return true;
  return Array.isArray(thread.taggedUserIds) && thread.taggedUserIds.length > 0;
}

export function statusPostHasContext(thread) {
  if (!thread) return false;
  const hasLocation = !!String(thread.locationLabel || '').trim();
  return hasLocation || threadHasTaggedPeople(thread);
}

/** @param {{ variant?: 'story' | 'article' }} [options] — article omits "at" / "is with" phrasing (feed cards). */
export function getStatusPostContextParts(thread, options = {}) {
  if (!thread) {
    return { authorName: '', locationName: '', locationLine: '', withLine: '' };
  }
  const variant = options.variant === 'article' ? 'article' : 'story';
  const authorName = thread.postAnonymous
    ? 'Anonymous'
    : thread.authorSnapshot?.name || thread.authorPreview?.name || 'Member';
  const location = toTitleCaseWords(thread.locationLabel);
  const taggedNames = taggedUserNamesFromThread(thread);
  const taggedCount = taggedNames.length || (Array.isArray(thread.taggedUserIds) ? thread.taggedUserIds.length : 0);

  let withLine = '';
  if (taggedNames.length === 1) {
    withLine = variant === 'article' ? taggedNames[0] : `is with ${taggedNames[0]}`;
  } else if (taggedNames.length === 2) {
    withLine =
      variant === 'article'
        ? `${taggedNames[0]} and ${taggedNames[1]}`
        : `is with ${taggedNames[0]} and ${taggedNames[1]}`;
  } else if (taggedNames.length > 2) {
    const others = taggedNames.length - 1;
    withLine =
      variant === 'article'
        ? `${taggedNames[0]} and ${others} other ${others === 1 ? 'person' : 'people'}`
        : `is with ${taggedNames[0]} and ${others} other ${others === 1 ? 'person' : 'people'}`;
  } else if (!taggedNames.length && taggedCount === 1) {
    withLine = variant === 'article' ? '1 person' : 'is with 1 person';
  } else if (!taggedNames.length && taggedCount > 1) {
    withLine = variant === 'article' ? `${taggedCount} people` : `is with ${taggedCount} people`;
  }

  return {
    authorName,
    locationName: location || '',
    locationLine: location ? (variant === 'article' ? location : `at ${location}`) : '',
    withLine,
  };
}

/** Facebook-style story headline: "Jane at San Francisco · with Alex and 2 other people". */
export function formatStatusPostHeadline(thread) {
  const { authorName, locationLine, withLine } = getStatusPostContextParts(thread);
  const withPart = withLine ? withLine.replace(/^is /, '') : '';
  if (locationLine && withPart) return `${authorName} ${locationLine} · ${withPart}`;
  if (locationLine) return `${authorName} ${locationLine}`;
  if (withLine) return `${authorName} ${withLine}`;
  return authorName;
}

const STATUS_POST_SETUP_SEP = ' > ';

function decodeDiscussionHtmlEntities(text) {
  return String(text || '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&#160;/gi, ' ')
    .replace(/&#xA0;/gi, ' ')
    .replace(/\u00A0/g, ' ')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&apos;/gi, "'");
}

function statusPostAudiencePeopleLabel(thread) {
  const count = Array.isArray(thread?.audienceUserIds) ? thread.audienceUserIds.length : 0;
  if (count === 1) return '1 person';
  if (count > 1) return `${count} people`;
  return '';
}

/** Feed/compose breadcrumb for wall stories: "Story > My wall > Public". */
export function formatStatusPostSetupPath(thread) {
  if (!isStoryContentThread(thread)) return '';
  const audienceLabel = composeStoryAudienceHeadlineLabel(
    String(thread.audience || 'followers').trim().toLowerCase(),
    Array.isArray(thread.audienceUserIds) ? thread.audienceUserIds.length : 0,
    statusPostAudiencePeopleLabel(thread)
  );
  return ['Story', 'My wall', audienceLabel].join(STATUS_POST_SETUP_SEP);
}

/** Third segment of wall-story setup path (Public, Followers, Only me, etc.). */
export function storyWallAudienceLabel(thread) {
  if (!isStoryContentThread(thread)) return '';
  const parts = String(formatStatusPostSetupPath(thread) || '')
    .split(/\s*>\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  return parts[2] || '';
}

function statusPostSetupPathPattern(setupPath) {
  const parts = String(setupPath || '')
    .split(/\s*>\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return null;
  const sep = '\\s*(?:>|&gt;)\\s*';
  const body = parts.map((part) => part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join(sep);
  return new RegExp(`\\s+${body}\\s*$`, 'i');
}

/** Remove a trailing setup breadcrumb accidentally stored in a story title. */
export function stripStatusPostSetupFromTitle(title, thread) {
  const raw = decodeDiscussionHtmlEntities(String(title || '')).trim();
  if (!raw || !isStoryContentThread(thread)) return raw;

  const setupPath = formatStatusPostSetupPath(thread);
  if (!setupPath) return raw;

  let cleaned = raw;
  for (const suffix of [` ${setupPath}`, `\n${setupPath}`]) {
    if (cleaned.endsWith(suffix)) {
      cleaned = cleaned.slice(0, -suffix.length).trim();
      break;
    }
  }

  const pattern = statusPostSetupPathPattern(setupPath);
  if (pattern) cleaned = cleaned.replace(pattern, '').trim();

  return cleaned;
}

/** Block closings / openings that should become a line break when flattening HTML. */
const DISCUSSION_BLOCK_BREAK_TAG =
  /^(?:p|div|h[1-6]|blockquote|pre|li|tr|table|ul|ol|section|article|header|footer|aside|address|dl|dt|dd|figcaption|figure)$/i;

/** Strip HTML markup to plain text; handles feed-truncated partial tags (e.g. `<span style='…`). */
export function stripDiscussionHtmlMarkup(html, { lineBreaks = false } = {}) {
  const spaceToken = ' ';
  let text = String(html || '');
  text = text
    .replace(/<style[\s\S]*?<\/style>/gi, lineBreaks ? '\n' : spaceToken)
    .replace(/<script[\s\S]*?<\/script>/gi, lineBreaks ? '\n' : spaceToken);

  if (!lineBreaks) {
    return decodeDiscussionHtmlEntities(
      text.replace(/<[^>]+>/g, spaceToken).replace(/<[^>]*$/g, spaceToken)
    );
  }

  text = text
    .replace(/<\s*br\s*\/?>/gi, '\n')
    /* Adjacent block wrappers = one break (matches opened-post <p> stack; avoids \n\n from </p><p>). */
    .replace(/<\/\s*p\s*>\s*<\s*p\b[^>]*>/gi, '\n')
    .replace(/<\/\s*div\s*>\s*<\s*div\b[^>]*>/gi, '\n')
    .replace(/<\/\s*([a-z][a-z0-9]*)\s*>/gi, (match, tag) =>
      DISCUSSION_BLOCK_BREAK_TAG.test(tag) ? '\n' : ''
    )
    .replace(
      /<\s*(p|div|h[1-6]|blockquote|pre|ul|ol|table|section|article|li|tr|hr)\b[^>]*\/?>/gi,
      ''
    )
    .replace(/<[^>]+>/g, '')
    .replace(/<[^>]*$/g, '');

  return decodeDiscussionHtmlEntities(text);
}

/** Plain text from stored discussion body (HTML or markdown). */
export function discussionBodyPlainText(body) {
  return stripDiscussionHtmlMarkup(body, { lineBreaks: false })
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when stored body includes an HTML or markdown pipe table. */
export function topicBodyHasTable(body) {
  const t = String(body || '').trim();
  if (!t) return false;
  if (/<table\b/i.test(t)) return true;
  const lines = t.split('\n');
  for (let i = 0; i < lines.length - 1; i++) {
    if (lines[i].includes('|') && /^\s*\|?\s*:?[-| :]+\|/.test(lines[i + 1])) return true;
  }
  return false;
}

/** Plain-text body excerpt for question/announcement feed cards (line breaks preserved). */
export function storyLikeFeedBodyText(thread) {
  if (!usesStoryModalThreadLayout(thread) || isStoryContentThread(thread)) return '';
  return formatTopicExcerpt(thread.body);
}

/** Story feed card text from body (keeps paragraph breaks; inline bold/italic stay inline). */
export function storyFeedPrimaryText(thread) {
  if (!isStoryContentThread(thread)) return '';
  const fromBody = formatTopicExcerpt(thread.body);
  if (fromBody) return fromBody;
  return stripStatusPostSetupFromTitle(thread.title, thread);
}

/** Club posts show title + body snippet; story-like posts use expandable text — skip duplicate snippet. */
export function shouldShowTopicFeedSnippet(thread, excerpt) {
  const text = String(excerpt || '').trim();
  if (!text && !topicBodyHasTable(thread?.body)) return false;
  if (topicBodyHasTable(thread?.body)) return true;
  if (!text) return false;
  if (usesStoryModalThreadLayout(thread)) return false;
  return true;
}

function normalizeThreadTitlePlain(title, thread) {
  const cleanedTitle = isStoryContentThread(thread)
    ? stripDiscussionHashtagTokens(stripStatusPostSetupFromTitle(title, thread))
    : stripDiscussionHashtagTokens(title);
  return discussionBodyPlainText(cleanedTitle);
}

/** Hide thread h1 when stored title is just an excerpt of the body (wall stories, etc.). */
export function shouldShowThreadViewTitle(thread) {
  if (!thread) return false;

  const titlePlain = normalizeThreadTitlePlain(thread.title, thread);
  if (!titlePlain) return false;

  if (!isStoryContentThread(thread)) return true;

  const bodyPlain = discussionBodyPlainText(thread.body);
  if (!bodyPlain) return true;
  if (titlePlain === bodyPlain) return false;

  const shorter = titlePlain.length <= bodyPlain.length ? titlePlain : bodyPlain;
  const longer = shorter === titlePlain ? bodyPlain : titlePlain;
  if (shorter.length >= 12 && longer.startsWith(shorter)) return false;

  return true;
}

export function threadViewDisplayTitle(thread) {
  if (!thread) return '';
  const title = isStoryContentThread(thread)
    ? stripStatusPostSetupFromTitle(thread.title, thread)
    : String(thread.title || '');
  return String(title || '').trim();
}

export function formatTopicExcerpt(body, max = 1200) {
  if (!body || typeof body !== 'string') return '';
  const plainText = stripDiscussionHtmlMarkup(body, { lineBreaks: true })
    .replace(/[ \t]+\n/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!plainText.length) return '';
  if (plainText.length <= max) return plainText;
  return `${plainText.slice(0, max - 1)}…`;
}

export function resolveDiscussionAssetUrl(raw) {
  const u = String(raw || '').trim();
  if (!u) return '';
  if (u.startsWith('//')) return `https:${u}`;
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (parsed.pathname.startsWith('/uploads/')) {
        return `${parsed.pathname}${parsed.search}`;
      }
    } catch {
      /* ignore */
    }
    return u;
  }
  return `/${u.replace(/^\//, '')}`;
}

export function contributorAvatarSrc(
  profilePictureUrl,
  apiBase,
  profileMode = 'personal',
  identity = {}
) {
  const unique = () =>
    getUniqueDefaultAvatarDataUrl({
      userId: identity?.userId,
      name: identity?.name,
    });

  const u = String(profilePictureUrl || '').trim();
  if (isPlaceholderProfilePictureUrl(u)) {
    return unique();
  }
  let resolved = '';
  if (/^https?:\/\//i.test(u)) {
    try {
      const parsed = new URL(u);
      if (parsed.pathname.startsWith('/uploads/')) {
        resolved = `${parsed.pathname}${parsed.search}`;
      } else {
        resolved = u;
      }
    } catch {
      resolved = u;
    }
  } else {
    resolved = resolveDiscussionAssetUrl(u) || `${apiBase}/${String(u).replace(/^\//, '')}`;
  }
  if (!resolved || isPlaceholderProfilePictureUrl(resolved)) {
    return unique();
  }
  return resolved;
}

/** Full stat display for tooltips: 3500 → "3,500". */
export function formatFullCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  return new Intl.NumberFormat().format(Math.trunc(n));
}

/** Compact stat display: 999 → "999", 1000 → "1K", 1_500_000 → "1.5M". */
export function formatCompactCount(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  const abs = Math.abs(n);
  if (abs < 1000) return String(Math.trunc(n));

  const tiers = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];

  for (const [divisor, suffix] of tiers) {
    if (abs >= divisor) {
      const scaled = n / divisor;
      const absScaled = Math.abs(scaled);
      let text;
      if (absScaled >= 100) {
        text = String(Math.trunc(scaled));
      } else {
        text = scaled.toFixed(1).replace(/\.0$/, '');
      }
      return `${text}${suffix}`;
    }
  }

  return String(Math.trunc(n));
}

/** Merge API update response into a feed/thread row (body, title, hashtags, etc.). */
export function mergeDiscussionThreadAfterUpdate(existing, apiThread, localPatch = {}) {
  if (!existing) return existing;
  const api = apiThread && typeof apiThread === 'object' ? apiThread : {};
  return {
    ...existing,
    ...localPatch,
    ...(api.title != null ? { title: api.title } : {}),
    ...(api.body != null ? { body: api.body } : {}),
    ...(Array.isArray(api.hashtags) ? { hashtags: api.hashtags } : {}),
    ...(api.externalLink != null ? { externalLink: api.externalLink } : {}),
    ...(api.status != null ? { status: api.status } : {}),
    ...(api.updatedAt != null ? { updatedAt: api.updatedAt } : {}),
  };
}

/** Whole calendar days between an ISO timestamp and today (local midnight boundaries). */
export function calendarDaysSince(iso) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return NaN;
  const now = new Date();
  const thenDay = new Date(then.getFullYear(), then.getMonth(), then.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.floor((today.getTime() - thenDay.getTime()) / 86400000);
}

/** Whole calendar years between an ISO timestamp and today. */
export function calendarYearsSince(iso) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return NaN;
  const now = new Date();
  let years = now.getFullYear() - then.getFullYear();
  const monthDiff = now.getMonth() - then.getMonth();
  const dayDiff = now.getDate() - then.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) years -= 1;
  return years;
}

/** Whole calendar months between an ISO timestamp and today. */
export function calendarMonthsSince(iso) {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return NaN;
  const now = new Date();
  let months = (now.getFullYear() - then.getFullYear()) * 12 + (now.getMonth() - then.getMonth());
  if (now.getDate() < then.getDate()) months -= 1;
  return months;
}

export function formatRelativeShort(iso) {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 45) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  const years = calendarYearsSince(iso);
  if (Number.isFinite(years) && years >= 1) {
    return years === 1 ? 'a year ago' : `${years} years ago`;
  }
  const months = calendarMonthsSince(iso);
  if (Number.isFinite(months) && months >= 1) {
    return months === 1 ? 'a month ago' : `${months} months ago`;
  }
  const day = calendarDaysSince(iso);
  if (!Number.isFinite(day) || day < 1) return `${hr}h ago`;
  if (day >= 7) {
    const weeks = Math.floor(day / 7);
    return weeks === 1 ? 'a week ago' : `${weeks} weeks ago`;
  }
  return `${day}d ago`;
}

/** Optimistic vote patch for feed rows. vote: 1, -1, or 0 to clear. */
export function patchThreadVoteLocal(thread, vote) {
  if (!thread) return thread;
  const oldVote = thread.myVote != null ? Number(thread.myVote) : 0;
  let upvotes = Number(thread.upvoteCount ?? thread.voteUpCount);
  let downvotes = Number(thread.downvoteCount ?? thread.voteDownCount);
  const hasSplitCounts = Number.isFinite(upvotes) && Number.isFinite(downvotes);
  let score = Number(thread.voteScore) || 0;

  if (oldVote === 1) {
    score -= 1;
    if (hasSplitCounts) upvotes = Math.max(0, upvotes - 1);
  }
  if (oldVote === -1) {
    score += 1;
    if (hasSplitCounts) downvotes = Math.max(0, downvotes - 1);
  }

  const nextVote = vote === 0 ? null : vote;
  if (nextVote === 1) {
    score += 1;
    if (hasSplitCounts) upvotes += 1;
  }
  if (nextVote === -1) {
    score -= 1;
    if (hasSplitCounts) downvotes += 1;
  }

  const patch = {
    ...thread,
    voteScore: score,
    myVote: nextVote,
  };
  if (hasSplitCounts) {
    patch.upvoteCount = upvotes;
    patch.downvoteCount = downvotes;
    patch.voteUpCount = upvotes;
    patch.voteDownCount = downvotes;
  }
  return patch;
}

export function threadVotePatchFromApi(data, fallback = {}) {
  const upvoteCount = data?.upvoteCount ?? data?.voteUpCount ?? fallback.upvoteCount ?? fallback.voteUpCount ?? 0;
  const downvoteCount =
    data?.downvoteCount ?? data?.voteDownCount ?? fallback.downvoteCount ?? fallback.voteDownCount ?? 0;
  return {
    voteScore: data?.voteScore ?? fallback.voteScore ?? 0,
    upvoteCount,
    downvoteCount,
    voteUpCount: upvoteCount,
    voteDownCount: downvoteCount,
    myVote: data?.myVote != null ? data.myVote : null,
  };
}
